import React, { useMemo } from "react";
import {
  Alert,
  Breadcrumb,
  Card,
  Collapse,
  Divider,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { HomeOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import {
  ACTOR_COLORS,
  ACTOR_LABELS,
  APP_AUTOMATIONS,
  LEANCUSTOMS_UTB_TTW_AUTOMATION_ID,
  REACTION_KIND_COLORS,
  REACTION_KIND_LABELS,
  SHOW_UNLOADING_STATUS_AUTOMATIONS,
  STATUS_REACTIONS,
  getVisibleStatusUpdaters,
  UNLOADING_STATUS_VALUES,
  type AppAutomation,
  type BookingStatusField,
  type StatusReaction,
  type StatusUpdater,
} from "../config/bookingAutomations";
import { AutomationRunHistory } from "../components/automations/AutomationRunHistory";
import { AutomationCustomsClearedPreview } from "../components/automations/AutomationCustomsClearedPreview";
import { validStatuses } from "../utils/bookingStatus";

const { Title, Text, Paragraph } = Typography;

const PageContainer = styled.div`
  padding: 16px;
`;

const CodeText = styled(Text)`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
`;

const AutomationsAdmin: React.FC = () => {
  React.useEffect(() => {
    trackFeatureUsage("admin_automations");
  }, []);

  const visibleUpdaters = useMemo(() => getVisibleStatusUpdaters(), []);

  const reactionsByStatus = useMemo(() => {
    const map = new Map<string, StatusReaction[]>();
    const n8nStatuses = new Set(["submitted", "approved", "rejected"]);
    const wildcardReactions = STATUS_REACTIONS.filter((r) =>
      r.whenStatus.startsWith("*")
    );

    for (const status of validStatuses) {
      const exact = STATUS_REACTIONS.filter(
        (r) => r.field === "status" && r.whenStatus === status
      );
      const wildcards = n8nStatuses.has(status)
        ? []
        : wildcardReactions.filter((r) => r.field === "status");
      map.set(`status:${status}`, [...exact, ...wildcards]);
    }

    if (SHOW_UNLOADING_STATUS_AUTOMATIONS) {
      for (const status of UNLOADING_STATUS_VALUES) {
        map.set(
          `unloading_status:${status}`,
          STATUS_REACTIONS.filter(
            (r) => r.field === "unloading_status" && r.whenStatus === status
          )
        );
      }
    }

    return map;
  }, []);

  const plannedReactions = STATUS_REACTIONS.filter((r) => r.kind === "planned");

  const appAutomationColumns = [
    {
      title: "Automation",
      dataIndex: "label",
      key: "label",
      width: 240,
      render: (label: string, record: AppAutomation) => (
        <Space direction="vertical" size={2}>
          <Text strong>{label}</Text>
          {record.edgeFunction && (
            <CodeText>{record.edgeFunction}</CodeText>
          )}
        </Space>
      ),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      width: 180,
    },
    {
      title: "When",
      dataIndex: "condition",
      key: "condition",
    },
    {
      title: "Then",
      dataIndex: "action",
      key: "action",
      width: 220,
    },
    {
      title: "Also",
      dataIndex: "sideEffects",
      key: "sideEffects",
      width: 200,
      render: (sideEffects: string[] | undefined, record: AppAutomation) => (
        <Space direction="vertical" size={2}>
          {sideEffects?.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
              {sideEffects.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <Text type="secondary">—</Text>
          )}
          {record.schedule && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.schedule}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const updaterColumns = [
    {
      title: "Trigger",
      dataIndex: "label",
      key: "label",
      width: 260,
      render: (label: string, record: StatusUpdater) => (
        <Space direction="vertical" size={2}>
          <Text strong>{label}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.trigger}
          </Text>
        </Space>
      ),
    },
    ...(SHOW_UNLOADING_STATUS_AUTOMATIONS
      ? [
          {
            title: "Field",
            dataIndex: "field",
            key: "field",
            width: 130,
            render: (field: BookingStatusField) => (
              <Tag color={field === "status" ? "blue" : "volcano"}>{field}</Tag>
            ),
            filters: [
              { text: "status", value: "status" },
              { text: "unloading_status", value: "unloading_status" },
            ],
            onFilter: (value: React.Key | boolean, record: StatusUpdater) =>
              record.field === value,
          },
        ]
      : []),
    {
      title: "Sets to",
      dataIndex: "setsTo",
      key: "setsTo",
      width: 220,
      render: (setsTo: string) => <Text code>{setsTo}</Text>,
    },
    {
      title: "Actor",
      dataIndex: "actor",
      key: "actor",
      width: 110,
      render: (actor: StatusUpdater["actor"]) => (
        <Tag color={ACTOR_COLORS[actor]}>{ACTOR_LABELS[actor]}</Tag>
      ),
      filters: Object.entries(ACTOR_LABELS).map(([value, text]) => ({
        text,
        value,
      })),
      onFilter: (value: React.Key | boolean, record: StatusUpdater) =>
        record.actor === value,
    },
    {
      title: "Also does",
      dataIndex: "alsoDoes",
      key: "alsoDoes",
      width: 200,
      render: (alsoDoes: string[] | undefined, record: StatusUpdater) => (
        <Space direction="vertical" size={2}>
          {alsoDoes?.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
              {alsoDoes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <Text type="secondary">—</Text>
          )}
          {record.notes && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.notes}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const statusOverviewRows = useMemo(
    () =>
      validStatuses.map((status) => ({
        key: `status-${status}`,
        field: "status" as const,
        status,
        reactions: reactionsByStatus.get(`status:${status}`) ?? [],
      })),
    [reactionsByStatus]
  );

  const unloadingOverviewRows = useMemo(
    () =>
      UNLOADING_STATUS_VALUES.map((status) => ({
        key: `unloading-${status}`,
        field: "unloading_status" as const,
        status,
        reactions: reactionsByStatus.get(`unloading_status:${status}`) ?? [],
      })),
    [reactionsByStatus]
  );

  const reactionOverviewColumns = [
    {
      title: "Status value",
      dataIndex: "status",
      key: "status",
      width: 220,
      render: (status: string) => <Text code>{status}</Text>,
    },
    {
      title: "When booking reaches this status",
      dataIndex: "reactions",
      key: "reactions",
      render: (reactions: StatusReaction[]) => {
        if (reactions.length === 0) {
          return (
            <Text type="secondary" italic>
              No automations configured
            </Text>
          );
        }
        return (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {reactions.map((r) => (
              <div key={r.id}>
                <Space wrap size={4}>
                  <Tag color={REACTION_KIND_COLORS[r.kind]}>
                    {REACTION_KIND_LABELS[r.kind]}
                  </Tag>
                  <Text strong>{r.label}</Text>
                </Space>
                <Paragraph
                  type="secondary"
                  style={{ margin: "4px 0 0", fontSize: 12 }}
                >
                  {r.description}
                </Paragraph>
                {r.location && (
                  <CodeText style={{ fontSize: 11 }}>{r.location}</CodeText>
                )}
              </div>
            ))}
          </Space>
        );
      },
    },
  ];

  const statusReactionSection = (
    <Table
      rowKey="key"
      columns={reactionOverviewColumns}
      dataSource={statusOverviewRows}
      pagination={false}
      size="small"
    />
  );

  return (
    <PageContainer>
      <AdminNavBar />

      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/admin">
                <HomeOutlined /> Admin
              </Link>
            ),
          },
          { title: "Automations" },
        ]}
      />

      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            <ThunderboltOutlined style={{ marginRight: 8 }} />
            Automations
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Reference for what can change <Text code>bookings.status</Text> and
            what runs when a status is reached. Update{" "}
            <CodeText>src/config/bookingAutomations.ts</CodeText> when behaviour
            changes.
          </Paragraph>
        </div>

        <Alert
          type="info"
          showIcon
          message={`${APP_AUTOMATIONS.length} active app automation(s)`}
          description="Section 2 covers condition-based status automations (preview, run history). Section 3 lists DB email reactions when a status is reached."
        />

        <Card
          title="1. What can update a status?"
          extra={
            <Text type="secondary">{visibleUpdaters.length} writers</Text>
          }
        >
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Every path that writes <Text code>bookings.status</Text> today.
            “Also does” lists side effects in the same user action (not separate
            on-land automations).
          </Paragraph>
          <Table
            rowKey="id"
            columns={updaterColumns}
            dataSource={visibleUpdaters}
            pagination={false}
            size="small"
            scroll={{ x: 1100 }}
          />
        </Card>

        <Card
          title="2. Active app automations (conditions → status change)"
          extra={
            <Text type="secondary">{APP_AUTOMATIONS.length} automation(s)</Text>
          }
        >
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            These run on a schedule or event and may update{" "}
            <Text code>bookings.status</Text> when conditions are met. They only
            apply while the booking is still in an eligible source status.
          </Paragraph>
          <Table
            rowKey="id"
            columns={appAutomationColumns}
            dataSource={APP_AUTOMATIONS}
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
          />

          <Divider style={{ margin: "24px 0 16px" }} />

          <Collapse
            defaultActiveKey={[LEANCUSTOMS_UTB_TTW_AUTOMATION_ID]}
            items={APP_AUTOMATIONS.map((automation) => ({
              key: automation.automationId ?? automation.id,
              label: (
                <Space>
                  <Text strong>{automation.label}</Text>
                  {automation.edgeFunction && (
                    <CodeText>{automation.edgeFunction}</CodeText>
                  )}
                </Space>
              ),
              children:
                automation.automationId === LEANCUSTOMS_UTB_TTW_AUTOMATION_ID ? (
                  <Space direction="vertical" size={24} style={{ width: "100%" }}>
                    <div>
                      <Text strong>Run now</Text>
                      <Paragraph
                        type="secondary"
                        style={{ marginTop: 4, marginBottom: 12 }}
                      >
                        Run the check manually; eligible bookings with UTB + TTW
                        are updated to{" "}
                        <Text code>customs_cleared</Text> automatically. Cron
                        also runs every 15 minutes when configured (see function
                        README).
                      </Paragraph>
                      <AutomationCustomsClearedPreview />
                    </div>
                    <div>
                      <Text strong>Run history</Text>
                      <Paragraph
                        type="secondary"
                        style={{ marginTop: 4, marginBottom: 12 }}
                      >
                        Audit log for scheduled and manual runs. Expand a run for
                        per-booking outcomes.
                      </Paragraph>
                      <AutomationRunHistory
                        automationId={LEANCUSTOMS_UTB_TTW_AUTOMATION_ID}
                      />
                    </div>
                  </Space>
                ) : (
                  <Text type="secondary">No monitoring UI for this automation yet.</Text>
                ),
            }))}
          />
        </Card>

        <Card
          title="3. What runs when a status is reached?"
          extra={
            <Text type="secondary">
              Per status value · DB + email reactions
            </Text>
          }
        >
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Reactions fire when a booking <strong>lands on</strong> that status
            (typically after an update in section 1 or 2). Email flows are DB
            triggers.
          </Paragraph>

          {SHOW_UNLOADING_STATUS_AUTOMATIONS ? (
            <Collapse
              defaultActiveKey={["lifecycle"]}
              items={[
                {
                  key: "lifecycle",
                  label: `bookings.status (${validStatuses.length} values)`,
                  children: statusReactionSection,
                },
                {
                  key: "unloading",
                  label: `bookings.unloading_status (${UNLOADING_STATUS_VALUES.length} values)`,
                  children: (
                    <Table
                      rowKey="key"
                      columns={reactionOverviewColumns}
                      dataSource={unloadingOverviewRows}
                      pagination={false}
                      size="small"
                    />
                  ),
                },
              ]}
            />
          ) : (
            statusReactionSection
          )}

          {plannedReactions.length > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message="Planned app automations"
              description={
                <Space direction="vertical" size={4}>
                  {plannedReactions.map((r) => (
                    <Text key={r.id}>{r.description}</Text>
                  ))}
                </Space>
              }
            />
          )}
        </Card>
      </Space>
    </PageContainer>
  );
};

export default AutomationsAdmin;
